<div class="admin-actions">
  <div class="page-header">
    <div class="header-content">
      <div class="header-left">
        <button class="btn-back" (click)="goBack()">
          <span class="back-icon">⬅️</span>
          Back to Admin
        </button>
        <div class="title-section">
          <h1 class="page-title">Admin Action History</h1>
          <p class="page-subtitle">
            View and edit your recent actions (within 7 days)
          </p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn-refresh" (click)="onRefresh()" [disabled]="loading">
          <span class="refresh-icon">🔄</span>
          <span>Refresh</span>
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="messages.success" class="alert alert-success">
    <span>{{ messages.success }}</span>
    <button class="alert-close" (click)="dismissMessage('success')">×</button>
  </div>

  <div *ngIf="messages.error" class="alert alert-error">
    <span>{{ messages.error }}</span>
    <button class="alert-close" (click)="dismissMessage('error')">×</button>
  </div>

  <div *ngIf="messages.warning" class="alert alert-warning">
    <span>{{ messages.warning }}</span>
    <button class="alert-close" (click)="dismissMessage('warning')">×</button>
  </div>

  <div *ngIf="loading" class="loading-container">
    <div class="loading-spinner"></div>
    <p>Loading action history...</p>
  </div>

  <div *ngIf="error && !loading" class="error-container">
    <div class="error-card">
      <h3>Error Loading Action History</h3>
      <p>{{ error }}</p>
      <button class="btn-retry" (click)="onRefresh()">Try Again</button>
    </div>
  </div>

  <div *ngIf="!loading && !error" class="actions-section">
    <div class="actions-header">
      <div class="actions-count">
        Showing {{ actions.length }} action(s) from the past week
      </div>
      <div class="info-text">
        <span class="info-icon">ℹ️</span>
        You can edit actions within 7 days of performing them
      </div>
    </div>

    <div *ngIf="actions.length === 0" class="empty-state">
      <div class="empty-content">
        <div class="empty-icon">📋</div>
        <h3>No Recent Actions</h3>
        <p>You haven't performed any actions in the past week.</p>
      </div>
    </div>

    <div *ngIf="actions.length > 0" class="actions-list">
      <div
        *ngFor="let action of actions; trackBy: trackByActionId"
        class="action-card"
        [class]="getActionClass(action.action_type)"
        [class.latest-action]="highlightLatestAction(action.id)"
      >
        <div class="action-header">
          <div class="action-icon">{{ getActionIcon(action.action_type) }}</div>
          <div class="action-info">
            <h3 class="action-title">
              {{ formatActionType(action.action_type) }}
              <span
                *ngIf="highlightLatestAction(action.id)"
                class="latest-badge"
              >
                LATEST
              </span>
              <span *ngIf="isModifiedAction(action)" class="modified-badge">
                UPDATED
              </span>
            </h3>
            <p class="action-subtitle">{{ getListingTitle(action) }}</p>
            <p
              *ngIf="getOriginalActionInfo(action)"
              class="original-action-info"
            >
              {{ getOriginalActionInfo(action) }}
            </p>
          </div>
          <div class="action-meta">
            <div class="action-date">{{ formatDate(action.created_at) }}</div>
            <div *ngIf="canEdit(action)" class="edit-info">
              <span class="edit-badge"
                >{{ getRemainingEditDays(action) }} days left to edit</span
              >
            </div>
          </div>
        </div>

        <div class="action-details">
          <div class="detail-item">
            <span class="detail-label">Target ID:</span>
            <span class="detail-value">#{{ action.target_id }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Reason/Note:</span>
            <span class="detail-value">{{ getActionReason(action) }}</span>
          </div>
        </div>

        <div *ngIf="canEdit(action)" class="action-footer">
          <button
            class="btn-edit"
            (click)="openEditModal(action)"
            [disabled]="processing"
          >
            <span class="btn-icon">✏️</span>
            Edit Action
          </button>
        </div>

        <div *ngIf="!canEdit(action)" class="action-footer disabled">
          <span class="edit-disabled">Edit period expired</span>
        </div>
      </div>
    </div>

    <div *ngIf="totalPages > 1" class="pagination">
      <button
        class="btn-page"
        [disabled]="currentPage === 1"
        (click)="onPageChange(currentPage - 1)"
      >
        ← Previous
      </button>

      <button
        *ngIf="shouldShowEllipsis('start')"
        class="btn-page"
        (click)="onPageChange(1)"
      >
        1
      </button>

      <span *ngIf="shouldShowEllipsis('start')" class="pagination-ellipsis"
      >...</span
      >

      <button
        *ngFor="let page of getPageNumbers()"
        class="btn-page"
        [class.active]="page === currentPage"
        (click)="onPageChange(page)"
      >
        {{ page }}
      </button>

      <span *ngIf="shouldShowEllipsis('end')" class="pagination-ellipsis"
      >...</span
      >

      <button
        *ngIf="shouldShowEllipsis('end')"
        class="btn-page"
        (click)="onPageChange(totalPages)"
      >
        {{ totalPages }}
      </button>

      <button
        class="btn-page"
        [disabled]="currentPage === totalPages"
        (click)="onPageChange(currentPage + 1)"
      >
        Next →
      </button>
    </div>
  </div>

  <div *ngIf="showEditModal" class="modal-overlay" (click)="closeEditModal()">
    <div class="modal-content" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <h3>Edit Admin Action</h3>
        <button class="modal-close" (click)="closeEditModal()">×</button>
      </div>

      <div class="modal-body">
        <div class="current-action">
          <h4>Current Action:</h4>
          <p>
            <strong>{{
              formatActionType(selectedAction?.action_type || "")
            }}</strong>
          </p>
          <p>{{ getListingTitle(selectedAction!) }}</p>
        </div>

        <div *ngIf="showLatestStatusWarning" class="latest-status-warning">
          <div class="warning-header">
            <span class="warning-icon">⚠️</span>
            <h4>Cannot Edit Outdated Status</h4>
          </div>
          <p>{{ messages.warning }}</p>
          <div *ngIf="latestActionInfo" class="latest-action-info">
            <h5>Most Recent Action:</h5>
            <div class="latest-action-details">
              <span class="action-type">{{
                formatActionType(latestActionInfo.action_type)
              }}</span>
              <span class="action-date">{{
                formatDate(latestActionInfo.created_at)
              }}</span>
              <span class="action-admin"
                >by {{ latestActionInfo.admin_name }}</span
              >
            </div>
          </div>
          <p class="instruction-text">
            Please locate the most recent action entry above to make changes.
          </p>
        </div>

        <div *ngIf="!showLatestStatusWarning" class="form-group">
          <label>Change to:</label>
          <div class="radio-group">
            <label class="radio-option">
              <input
                type="radio"
                name="newAction"
                value="approve"
                [(ngModel)]="newActionType"
              />
              <span class="radio-label">
                <span class="radio-icon">✅</span>
                Approve
              </span>
            </label>
            <label class="radio-option">
              <input
                type="radio"
                name="newAction"
                value="decline"
                [(ngModel)]="newActionType"
              />
              <span class="radio-label">
                <span class="radio-icon">❌</span>
                Decline
              </span>
            </label>
          </div>
        </div>

        <div
          *ngIf="newActionType === 'decline' && !showLatestStatusWarning"
          class="form-group"
        >
          <label for="edit-reason">Reason for Decline (Required):</label>
          <textarea
            id="edit-reason"
            [(ngModel)]="editReason"
            placeholder="Please provide a reason for declining this listing..."
            class="form-textarea"
            rows="4"
            required
          ></textarea>
        </div>

        <div *ngIf="!showLatestStatusWarning" class="warning-box">
          <span class="warning-icon">⚠️</span>
          <p>
            This will update the listing status and send a new email to the
            user. This action cannot be undone.
          </p>
        </div>
      </div>

      <div class="modal-actions">
        <button
          class="btn-modal cancel"
          (click)="closeEditModal()"
          [disabled]="processing"
        >
          {{ showLatestStatusWarning ? "Close" : "Cancel" }}
        </button>
        <button
          *ngIf="!showLatestStatusWarning"
          class="btn-modal confirm"
          (click)="confirmEdit()"
          [disabled]="
            processing || (newActionType === 'decline' && !editReason.trim())
          "
          [class.approve]="newActionType === 'approve'"
          [class.decline]="newActionType === 'decline'"
        >
          <span *ngIf="processing">Updating...</span>
          <span *ngIf="!processing"
            >Update to
            {{ newActionType === "approve" ? "Approve" : "Decline" }}</span
          >
        </button>
      </div>
    </div>
  </div>
</div>
